import { ButtonV2 } from '@synoptic/ui-kit/button-v2/button-v2.js';
import { LinkIcon } from '@synoptic/ui-kit/icons/react/link.js';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@synoptic/ui-kit/tooltip/tooltip.js';
import { clsx } from 'clsx';
import { type ComponentPropsWithRef, useEffect, useRef } from 'react';
import { StreamPostCardFragmentFragment } from '@/__generated__/graphql.ts';
import { PostCardMedia } from '@/components/post-card/post-card-media/post-card-media.tsx';
import { PostSummary } from '@/components/stream-post-card/summarize/post-summary.tsx';
import { SummarizePostButton } from '@/components/stream-post-card/summarize/summarize-post-button.tsx';
import {
  MIN_SUMMARIZABLE_POST_CHARS,
  useSummarizePost,
} from '@/components/stream-post-card/summarize/use-summarize-post.ts';
import { TextEditor } from '@/components/text-editor/text-editor.tsx';
import { ViewPriceChart } from '@/components/view-price-chart-link/view-price-chart-link.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { copyUrlToClipboard } from '@/utils/copy-url-to-clipboard.ts';
import {
  streamEventBodyV2,
  streamEventListV2,
  streamEventActionsV2,
  streamEventRowV2,
  streamEventTextV2,
  streamEventTimeV2,
  streamEventTopicV2,
} from './stream-data-v2.css.ts';

const eventTime = (createdAt: string) => {
  const created = new Date(createdAt);
  return {
    date: created.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    time: created.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
      hour12: false,
    }),
  };
};

export const StreamEventList = ({
  className,
  ...props
}: ComponentPropsWithRef<'div'>) => (
  <div className={clsx(streamEventListV2, className)} {...props} />
);

const selectionCrossesEditor = (root: HTMLElement, selection: Selection) => {
  const editor = root.querySelector('[role="textbox"]');
  if (!editor) return false;
  const { anchorNode, focusNode } = selection;
  if (!anchorNode || !focusNode) return false;
  return editor.contains(anchorNode) !== editor.contains(focusNode);
};

export const StreamEventRowV2 = ({
  post,
}: {
  post: {
    id: string;
    text: string;
    createdAt: string;
    deletedAt?: string | null;
    authorId: string;
    media: StreamPostCardFragmentFragment['media'];
  };
}) => {
  const toast = useToast();
  const stamp = eventTime(post.createdAt);
  const rowRef = useRef<HTMLElement>(null);
  const summary = useSummarizePost(post.id);
  const canSummarize =
    !post.deletedAt && post.text.length >= MIN_SUMMARIZABLE_POST_CHARS;

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;

    const onCopy = (event: ClipboardEvent) => {
      const selection = document.getSelection();
      if (!selection || selection.isCollapsed) return;
      if (!selectionCrossesEditor(row, selection)) return;
      const text = selection.toString();
      event.stopPropagation();
      if (!text) return;
      event.preventDefault();
      event.clipboardData?.setData('text/plain', text);
    };

    row.addEventListener('copy', onCopy, true);
    return () => row.removeEventListener('copy', onCopy, true);
  }, []);

  return (
    <article ref={rowRef} className={streamEventRowV2}>
      <div className={streamEventBodyV2}>
        {post.deletedAt ? (
          <p className={streamEventTextV2}>The post was deleted</p>
        ) : (
          <>
            {summary.isSummaryVisible ? (
              <PostSummary
                summary={summary.summary}
                loading={summary.isSummaryLoading}
              />
            ) : null}
            <TextEditor
              namespace={`stream-event:${post.id}`}
              className={streamEventTextV2}
              initialState={post.text}
              format="md"
              editable={false}
            />
            <PostCardMedia
              media={post.media}
              authorId={post.authorId}
              createdAt={post.createdAt}
            />
          </>
        )}
      </div>
      <div className={streamEventTopicV2}>
        <time className={streamEventTimeV2} dateTime={post.createdAt}>
          <span>{stamp.date},</span>
          <span>{stamp.time}</span>
        </time>
        <div className={streamEventActionsV2}>
          {canSummarize ? (
            <SummarizePostButton
              icon
              isSummaryVisible={summary.isSummaryVisible}
              onClick={summary.toggleSummary}
            />
          ) : null}
          <ViewPriceChart icon postId={post.id} />
          <Tooltip>
            <TooltipTrigger asChild>
              <ButtonV2
                type="button"
                variant="secondary"
                size="icon"
                aria-label="Copy link"
                onClick={() => {
                  copyUrlToClipboard(paths.makePost(post.id));
                  toast.info({ title: 'Link copied to clipboard' });
                }}
              >
                <LinkIcon />
              </ButtonV2>
            </TooltipTrigger>
            <TooltipContent side="bottom" description="Copy link" />
          </Tooltip>
        </div>
      </div>
    </article>
  );
};
